import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations } from '../utils/translations';

const STORAGE_KEY = 'nexia_lang';
const DEFAULT_LANG = 'fr';
const SUPPORTED = ['fr', 'de'];

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  // Lit la langue stockée. Si rien → null (déclenche la popup au 1er chargement).
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return SUPPORTED.includes(saved) ? saved : null;
    } catch { return null; }
  });

  const [hasChosen, setHasChosen] = useState(() => {
    try { return !!localStorage.getItem(STORAGE_KEY); }
    catch { return false; }
  });

  // Quand pas de langue choisie : applique le défaut visuellement mais garde
  // hasChosen=false pour déclencher la popup.
  const effectiveLang = lang || DEFAULT_LANG;

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setLangState(next);
    setHasChosen(true);
    document.documentElement.lang = next;
  }, []);

  // Met à jour <html lang> au montage
  useEffect(() => {
    document.documentElement.lang = effectiveLang;
  }, [effectiveLang]);

  /**
   * t('key.sub.path', { name: 'Sami' }) → texte traduit avec interpolation.
   * Fallback FR si la clé n'existe pas en DE.
   */
  const t = useCallback((path, vars) => {
    const get = (obj, key) => key.split('.').reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), obj);
    let value = get(translations[effectiveLang], path);
    if (value == null) value = get(translations.fr, path);
    if (value == null) return path;
    if (vars && typeof value === 'string') {
      return value.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? vars[k] : `{${k}}`));
    }
    return value;
  }, [effectiveLang]);

  return (
    <LanguageContext.Provider value={{ lang: effectiveLang, setLang, hasChosen, t, supported: SUPPORTED }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>');
  return ctx;
}
